/**
 * `?scene=compare&set=classic&cave=A&level=1`: one live cave, played by the autopilot, drawn four
 * ways at once - C64 Authentic, Enhanced, Remastered without lighting, Remastered - so the looks
 * can be judged side by side on the same moment of play.
 */

import { buildCave } from '../caves/build';
import type { CaveDef } from '../caves/types';
import { createAutopilot } from '../bot';
import type { Options } from '../platform/options';
import { DEFAULT_OPTIONS, withPreset } from '../platform/options';
import { BLACK, WHITE, YELLOW } from '../render/palette';
import { Screen } from '../render/screen';
import type { Scene } from '../ui/scene';
import { CaveRun } from './game/caveRun';
import { drawRun } from './game/gameView';
import { SILENT } from './game/runAudio';

interface Panel {
  readonly label: string;
  readonly opts: Options;
  readonly screen: Screen;
}

export function compareScene(def: CaveDef, level: number): Scene {
  const base = { ...DEFAULT_OPTIONS, wide: false };
  const panels: Panel[] = [
    { label: 'C64 AUTHENTIC', opts: withPreset(base, 'authentic') },
    { label: 'ENHANCED', opts: { ...withPreset(base, 'enhanced'), wide: false } },
    { label: 'REMASTERED, NO LIGHT', opts: { ...withPreset(base, 'remastered'), wide: false, lighting: false } },
    { label: 'REMASTERED', opts: { ...withPreset(base, 'remastered'), wide: false } },
  ].map((p) => ({ ...p, screen: new Screen(320, 200) }));
  const simOpts = panels[1]?.opts ?? DEFAULT_OPTIONS;
  let seed = 3;
  const start = (): CaveRun => {
    const pilot = createAutopilot({ seed });
    const run = new CaveRun(buildCave(def, level), seed++, {
      audio: SILENT,
      options: () => simOpts,
      score: () => 0,
      input: (s) => pilot.next(s),
      announce: () => undefined,
    });
    run.viewW = 320;
    run.snapCamera();
    return run;
  };
  let run = start();
  return {
    size: () => ({ w: 640, h: 400 }),
    update(dt) {
      run.update(dt);
      if (run.phase === 'done') run = start();
    },
    draw(screen) {
      panels.forEach((p, i) => {
        drawRun(p.screen, run, p.opts, { text: null, score: run.state.score });
        const w = p.label.length * 8 + 8;
        p.screen.fill(0, 189, w, 11, BLACK);
        p.screen.text(p.label, 4, 191, i === 3 ? YELLOW : WHITE);
        screen.image(p.screen.canvas, (i % 2) * 320, Math.floor(i / 2) * 200);
      });
    },
  };
}
